import { Component, DestroyRef, inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { BaseComponent } from 'src/app/models/base/base-component';
import { AccessedByPortalType } from '../../../../../interfaces/accessed-by-portal-type';
import { RequiredPermissions } from '../../../../../interfaces/required-permissions';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { AlertDetailsViewModel } from './alert-details-view-model';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertTypeEnum } from '../../../../../models/alerts/enum/alert-type.enum';
import { BehaviorSubject, combineLatest, defer, merge, Observable, of } from 'rxjs';
import { Alert } from '../../../../../models/alerts/dto/alert';
import { debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Breadcrumb } from '../../../../../models/shared/stylesheet/breadcrumb';
import { AlertStatusStringEnum } from '../../../../../models/alerts/enum/alert-status.enum';
import { ModalDeleteAlert } from '../../../../../modals/modal-delete-alert';
import { ComponentCanDeactivate } from '../../../../../models/protocols/component-can-deactivate';
import { ConfirmationOptions } from '../../../../../models/shared/stylesheet/confirmation-options';
import { ModalConfirmation } from '../../../../../modals/modal-confirmation';
import { PortalTypeIdEnum } from '../../../../../models/enum/shared/portal-type-id.enum';
import { BaseButtonSizeEnum } from '../../../../shared/components/base-button/base-button-size.enum';
import { MaskitoOptions } from '@maskito/core';
import { DropDownItem } from '../../../../../models/shared/stylesheet/drop-down-item';
import { RadioButton } from '../../../../../models/shared/radio-button';
import { AlertColor } from '../../../../../models/alerts/dto/alert-types';
import {
  AlertDetailsForm,
  buildAlertDetailsForm,
  combineTo24h,
  Meridiem,
  splitTime24h,
  toNativeDateString
} from './form-groups/alert-details-form-group';

export const MAX_ALERT_TITLE_LENGTH = 80;
export const MAX_ALERT_DESCRIPTION_LENGTH = 200;
export const MAX_ALERT_BUTTON_LENGTH = 40;

@Component({
  selector: 'app-alert-details',
  templateUrl: './alert-details.component.html',
  styleUrls: ['./alert-details.component.scss'],
  providers: [AlertDetailsViewModel]
})
export class AlertDetailsComponent
  extends BaseComponent
  implements AccessedByPortalType, RequiredPermissions, ComponentCanDeactivate
{
  constructor() {
    super();
  }

  protected viewModel = inject(AlertDetailsViewModel);
  private router = inject(Router);
  protected ngbModal = inject(NgbModal);
  protected injector = inject(Injector);
  protected destroyRef = inject(DestroyRef);

  public loadingOpts$ = this.viewModel.loadingOpts$;
  public isCreatingAlert$ = of(false);

  public alert$ = this.viewModel.alert$;
  public maxAlertTitleLength = MAX_ALERT_TITLE_LENGTH;
  public maxAlertDescriptionLength = MAX_ALERT_DESCRIPTION_LENGTH;
  public maxAlertButtonLength = MAX_ALERT_BUTTON_LENGTH;
  public isEditingAlert$ = this.viewModel.isEditingAlert$;

  public form: AlertDetailsForm = buildAlertDetailsForm(null);

  public readonly startTime12hControl = new FormControl<string>('', { nonNullable: true });
  public readonly startMeridiemControl = new FormControl<Meridiem>('AM', { nonNullable: true });
  public readonly endTime12hControl = new FormControl<string>('', { nonNullable: true });
  public readonly endMeridiemControl = new FormControl<Meridiem>('AM', { nonNullable: true });
  public readonly showPreviewControl = new FormControl<boolean>(true, { nonNullable: true });

  public readonly meridiemOptions: DropDownItem[] = [new DropDownItem('AM', 'AM'), new DropDownItem('PM', 'PM')];

  public readonly timeMaskOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, ':', /\d/, /\d/]
  };

  private _formSubmitAttempted = new BehaviorSubject<boolean>(false);
  public formSubmitAttempted$ = this._formSubmitAttempted.asObservable();

  public readonly customErrorMessages = {
    date: {
      required: $localize`Entry is required`,
      invalidDate: $localize`Must enter a date that follows the dd / mm / yyyy format`
    },
    startEndDate: {
      validStartEndDate: $localize`End date must be after start date`
    }
  };

  public isCardTypeAlert$ = this.viewModel.isCardTypeAlert$;
  public cardRadioButtons$ = this.viewModel.cardRadioButtons$;
  public bannerRadioButtons$ = this.viewModel.bannerRadioButtons$;
  public pageTitle$ = of($localize`Alert Details`);
  public actionButtonText$ = of($localize`Publish Changes`);
  public canEditAlertDetails$ = this.viewModel.canEditAlertDetails$;
  public statusPill$ = this.viewModel.statusPill$;
  public alertStartDate$ = this.viewModel.alertStartDate$;
  public alertEndDate$ = this.viewModel.alertEndDate$;
  public alertLastUpdatedDate$ = this.viewModel.alertLastUpdatedDate$;
  public previewLoading$ = this.viewModel.previewLoading$;
  public includeButton$ = this.viewModel.includeButton$;

  // Deferred so subclass overrides of isCreatingAlert$ are respected at subscription time
  public formEditable$ = defer(() => combineLatest([this.isEditingAlert$, this.isCreatingAlert$])).pipe(
    map(([isEditing, isCreating]) => isEditing || isCreating)
  );

  public canDeleteAlert$ = combineLatest([this.alert$, this.canEditAlertDetails$]).pipe(
    map(([alert, canEdit]) => {
      return canEdit && !!alert?.id && alert.status !== AlertStatusStringEnum.Past;
    })
  );

  public isPastAlert$ = this.alert$.pipe(map(a => a?.status === AlertStatusStringEnum.Past));

  public alertPreview$ = this.viewModel.alertPreview$;
  public showAlertPreview$ = this.viewModel.showAlertPreview$;

  public showCardAlertPreview$ = combineLatest([this.showAlertPreview$, this.isCardTypeAlert$]).pipe(
    map(([show, isCard]) => show && isCard)
  );

  public showBannerAlertPreview$ = combineLatest([this.showAlertPreview$, this.isCardTypeAlert$]).pipe(
    map(([show, isCard]) => show && !isCard)
  );

  public breadcrumbs$ = defer(() => combineLatest([this.alert$, this.isCreatingAlert$])).pipe(
    map(([alert, isCreating]) => {
      if (!!alert) {
        const breadcrumbs: Breadcrumb[] = [];
        breadcrumbs.push(new Breadcrumb($localize`Alerts`, '/settings/alerts'));
        if (isCreating) {
          breadcrumbs.push(new Breadcrumb($localize`Create an Alert`, '/settings/create-alert'));
        } else {
          breadcrumbs.push(new Breadcrumb($localize`Alert Details`, `/settings/alerts/${alert?.id}`));
        }

        return breadcrumbs;
      }
    })
  );

  public alertIsActive$ = this.alert$.pipe(map(a => a.status === AlertStatusStringEnum.Active));

  public readonly portalTypeRadioButtons: RadioButton[] = [
    new RadioButton($localize`Member Portal`, PortalTypeIdEnum.Member),
    new RadioButton($localize`Employer Portal`, PortalTypeIdEnum.Employer),
    new RadioButton($localize`Internal Portal`, PortalTypeIdEnum.Internal)
  ];

  goBack(): void {
    this.router.navigate(['/settings/alerts']).then();
  }

  public trackByRadioValue(index: number, radioButton: RadioButton): number {
    return radioButton.value;
  }

  public openDeleteModal(): void {
    this.alert$.once(alert => {
      ModalDeleteAlert.open(this.ngbModal, this.injector).then(result => {
        if (result) {
          this.viewModel.deleteAlert(alert.id.toString());
        }
      });
    });
  }

  private onAlertTypeSelected(type: AlertTypeEnum): void {
    this.alert$.once(alert => {
      if (alert.typeId !== type && !!alert?.id) {
        const opts = new ConfirmationOptions();
        let styleType: string;
        if (type === AlertTypeEnum.Card) {
          styleType = $localize`Card`;
        } else {
          styleType = $localize`Banner`;
        }
        opts.title = $localize`Changes to Message Content`;
        opts.bodyText = $localize`Changing the message style to ${styleType} will impact the button fields and
        description. Are you sure you want to change the style?`;
        opts.continueText = $localize`Change Message Style`;
        opts.cancelText = $localize`Cancel`;
        ModalConfirmation.open(this.ngbModal, this.injector, opts, result => {
          if (result) {
            this.applyAlertType(type);
          } else {
            this.form.controls.typeId.setValue(alert.typeId, { emitEvent: false });
            this.viewModel.updatePreview(this.buildAlertFromForm(alert));
          }
        });
      } else {
        this.applyAlertType(type);
      }
    });
  }

  private applyAlertType(type: AlertTypeEnum): void {
    this.viewModel.setAlertType(type);
    if (type === AlertTypeEnum.Card) {
      this.form.controls.includeButton.setValue(true);
    }
    this.viewModel.setMessageStyleChanged(true);
  }

  public toggleIsEditingAlert(): void {
    this.viewModel.toggleIsEditingAlert();
  }

  public cancelClicked(): void {
    this.viewModel.messageStyleChanged$.once(styleChanged => {
      if (this.form.dirty || styleChanged) {
        this.goBack();
      } else {
        this.toggleIsEditingAlert();
      }
    });
  }

  public onSubmit(): void {
    this._formSubmitAttempted.next(true);
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.alert$.once(alert => this.submitForm(this.buildAlertFromForm(alert)));
  }

  public submitForm(alert: Alert): void {
    this.viewModel.createAlert(alert);
  }

  public getAllowedPortalTypes(): PortalType[] {
    return [PortalType.Internal];
  }

  public canDeactivate(): boolean | Promise<boolean> | Observable<boolean> {
    return combineLatest([
      this.viewModel.alertFormDirty$,
      this.viewModel.alert$,
      this.viewModel.messageStyleChanged$
    ]).pipe(
      switchMap(([dirty, alert, styleChanged]) => {
        const title = alert?.id
          ? $localize`Are you sure you want to discard your changes?`
          : $localize`Are you sure you want to cancel this alert?`;
        const bodyText = alert?.id
          ? $localize`By cancelling, you will lose any changes you’ve made to the alert and the updates
          will not be published.`
          : $localize`Any information entered will be discarded and you will be returned to the Alerts Table.`;
        if (dirty || styleChanged) {
          const opts = new ConfirmationOptions();
          opts.title = title;
          opts.bodyText = bodyText;
          opts.continueText = $localize`Cancel Changes`;
          opts.cancelText = $localize`Close`;
          return ModalConfirmation.open(this.ngbModal, this.injector, opts, undefined, true) as Observable<boolean>;
        } else {
          return of(true);
        }
      })
    );
  }

  getRequiredPermissions(): number[] {
    return [1028];
  }

  setupBindings(): void {
    this.bindFormSeeding();
    this.bindFormEditability();
    this.bindTimeControlSync(this.startTime12hControl, this.startMeridiemControl, this.form.controls.startTime);
    this.bindTimeControlSync(this.endTime12hControl, this.endMeridiemControl, this.form.controls.endTime);
    this.bindPreviewUpdates();
    this.bindAlertTypeChanges();
    this.bindIncludeButton();
    this.bindButtonFieldValidators();
    this.bindColourReconciliation();
    this.bindShowPreviewToggle();
  }

  setupViews(): void {}

  private bindFormSeeding(): void {
    this.alert$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(alert => this.seedFormFromAlert(alert));
  }

  private seedFormFromAlert(alert: Alert): void {
    this.form.patchValue(
      {
        typeId: alert.typeId,
        colourId: alert.colourId ?? null,
        dismissable: alert.dismissable ?? false,
        includeButton: alert.includeButton ?? true,
        title: alert.title ?? null,
        description: alert.description ?? null,
        buttonText: alert.buttonText ?? null,
        buttonLink: alert.buttonLink ?? null,
        startDateString: toNativeDateString(alert.startDateString),
        startTime: alert.startTime || null,
        endDateString: toNativeDateString(alert.endDateString),
        endTime: alert.endTime || null,
        portalTypeId: alert.portalTypeId ?? null
      },
      { emitEvent: false }
    );
    this.seedTimeControls(alert.startTime || null, this.startTime12hControl, this.startMeridiemControl);
    this.seedTimeControls(alert.endTime || null, this.endTime12hControl, this.endMeridiemControl);
    this.syncButtonFieldValidators(alert.typeId, alert.includeButton ?? true);
    this.form.markAsPristine();
    this.viewModel.setAlertFormDirty(false);
    this.viewModel.updatePreview(this.buildAlertFromForm(alert));
  }

  private seedTimeControls(
    time24h: string | null,
    time12hControl: FormControl<string>,
    meridiemControl: FormControl<Meridiem>
  ): void {
    const { time12h, meridiem } = splitTime24h(time24h);
    time12hControl.setValue(time12h, { emitEvent: false });
    meridiemControl.setValue(meridiem, { emitEvent: false });
  }

  private bindFormEditability(): void {
    this.formEditable$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(editable => {
      const controls = [
        this.form,
        this.startTime12hControl,
        this.startMeridiemControl,
        this.endTime12hControl,
        this.endMeridiemControl
      ];
      controls.forEach(control => {
        if (editable) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      });
    });
  }

  private bindTimeControlSync(
    time12hControl: FormControl<string>,
    meridiemControl: FormControl<Meridiem>,
    target: FormControl<string | null>
  ): void {
    merge(time12hControl.valueChanges, meridiemControl.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const combined = combineTo24h(time12hControl.value, meridiemControl.value);
        if (target.value !== combined) {
          target.setValue(combined);
          target.markAsDirty();
        }
      });
  }

  private bindPreviewUpdates(): void {
    this.form.valueChanges
      .pipe(withLatestFrom(this.alert$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([, alert]) => {
        this.viewModel.updatePreview(this.buildAlertFromForm(alert));
        this.viewModel.setAlertFormDirty(this.form.dirty);
      });

    merge(
      this.form.controls.title.valueChanges,
      this.form.controls.description.valueChanges,
      this.form.controls.buttonText.valueChanges,
      this.form.controls.buttonLink.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.viewModel.setAlertPreviewPristine(false));
  }

  private bindAlertTypeChanges(): void {
    this.form.controls.typeId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(type => this.onAlertTypeSelected(type));
  }

  private bindIncludeButton(): void {
    this.form.controls.includeButton.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(includeButton => this.viewModel.setIncludeButton(includeButton));
  }

  private bindButtonFieldValidators(): void {
    combineLatest([this.viewModel.selectedAlertType$, this.viewModel.includeButton$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([type, includeButton]) => this.syncButtonFieldValidators(type, includeButton));
  }

  private syncButtonFieldValidators(type: AlertTypeEnum, includeButton: boolean): void {
    const required = type === AlertTypeEnum.Card && includeButton;
    [this.form.controls.buttonText, this.form.controls.buttonLink].forEach(control => {
      if (required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  // When the message style changes, ensure the selected colour belongs to the new style's palette
  private bindColourReconciliation(): void {
    combineLatest([this.viewModel.selectedAlertType$, this.viewModel.cardTypes$])
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(([selectedType, cardTypes]) => {
        const possibleColourIds =
          cardTypes
            ?.find(ct => ct.id === selectedType)
            ?.colours?.map(c => c?.id)
            ?.filter((id): id is number => typeof id === 'number') ?? [];
        const currentColourId = this.form.controls.colourId.value;
        if (possibleColourIds.length && (currentColourId === null || !possibleColourIds.contains(currentColourId))) {
          this.form.controls.colourId.setValue(possibleColourIds[0]);
        }
      });
  }

  private bindShowPreviewToggle(): void {
    this.showPreviewControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(showPreview => this.viewModel.setShowAlertPreview(showPreview));
  }

  private buildAlertFromForm(base: Alert): Alert {
    const raw = this.form.getRawValue();
    const alert = Object.assign(new Alert(), base);
    alert.colour = Object.assign(new AlertColor(), base.colour);
    alert.typeId = raw.typeId;
    alert.colourId = raw.colourId ?? alert.colourId;
    alert.title = raw.title ?? '';
    alert.description = raw.description ?? '';
    alert.buttonText = raw.buttonText;
    alert.buttonLink = raw.buttonLink;
    alert.dismissable = raw.dismissable;
    alert.includeButton = raw.includeButton;
    alert.portalTypeId = raw.portalTypeId ?? alert.portalTypeId;
    alert.startDateString = raw.startDateString ?? '';
    alert.startTime = raw.startTime ?? '';
    alert.endDateString = raw.endDateString ?? '';
    alert.endTime = raw.endTime ?? '';
    return alert;
  }

  protected readonly AlertTypeEnum = AlertTypeEnum;
  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
